import { type AriaRole, type ReactElement, type Ref, cloneElement, useCallback, useRef, useState } from "react";

export function useIsTruncated<T extends HTMLElement = HTMLElement>() {
    const [isTruncated, setIsTruncated] = useState(false);
    const observerRef = useRef<ResizeObserver | undefined>(undefined);
    // Tracks the measured element so it can be passed as a Tooltip's triggerRef. When the truncated line
    // wraps interactive content (e.g. an inline Link), that content's own react-aria FocusableContext usage
    // competes with the TooltipTrigger's, leaving the tooltip's implicit triggerRef unreliable; an explicit
    // triggerRef anchored here keeps the tooltip positioned against the actual truncated element.
    const elementRef = useRef<T | null>(null);

    const ref = useCallback((el: T | null) => {
        observerRef.current?.disconnect();
        observerRef.current = undefined;
        elementRef.current = el;

        if (!el) {
            setIsTruncated(false);

            return;
        }

        // The 1px height tolerance absorbs subpixel rounding on single-line text.
        const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth || el.scrollHeight - el.clientHeight > 1);

        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(el);
        observerRef.current = observer;
    }, []);

    return [ref, isTruncated, elementRef] as const;
}

interface TruncatableProps {
    ref: Ref<HTMLElement>;
    isTruncated?: boolean;
    // Pass false when the child is already natively interactive (e.g. Link/<a>) to avoid overriding its role.
    role?: AriaRole | false;
    children: ReactElement<{ ref?: Ref<HTMLElement>; role?: AriaRole; tabIndex?: number }>;
}

export const Truncatable = ({ ref, isTruncated = true, role = "button", children }: TruncatableProps) => {
    if (!isTruncated) {
        return cloneElement(children, { ref });
    }

    return cloneElement(children, { ref, tabIndex: 0, ...(role && { role }) });
};
